import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, LOGO_URL } from "../lib/api";
import OfflineBanner from "./OfflineBanner";
import LeadFormDialog from "./LeadFormDialog";
import { log } from "../lib/logger";
import {
  LayoutDashboard, ListChecks, Map as MapIcon, BarChart3, Users, LogOut, Menu, X, Plus, CalendarClock, Settings as SettingsIcon,
} from "lucide-react";

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showQuickLead, setShowQuickLead] = useState(false);
  const [fabUsers, setFabUsers] = useState([]);

  const closeMobile = () => setMobileOpen(false);

  const nav = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/leads", label: "Leads", icon: ListChecks, testid: "nav-leads" },
    { to: "/follow-ups", label: "Follow-ups", icon: CalendarClock, testid: "nav-followups" },
    { to: "/map", label: "Map", icon: MapIcon, testid: "nav-map" },
    { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
    { to: "/team", label: "Team & Areas", icon: Users, testid: "nav-team" },
    { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
  ];

  const openQuickLead = async () => {
    try {
      const r = await api.get("/users");
      setFabUsers(r.data);
    } catch (err) {
      log.warn("[Sohom] FAB user load failed:", err);
      setFabUsers([]);
    }
    setShowQuickLead(true);
  };

  const Sidebar = (
    <>
      <div className="p-4 border-b border-slate-200 flex items-center gap-2.5 cursor-pointer" onClick={() => { navigate("/dashboard"); setMobileOpen(false); }}>
        <img src={LOGO_URL} alt="Sohom" className="w-9 h-9 rounded-md object-contain bg-white" />
        <div className="min-w-0">
          <div className="font-bold text-[15px] tracking-tight text-slate-900 truncate" style={{fontFamily:"'Outfit', sans-serif"}}>Sohom Lead Ops</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider">Fenster Tech</div>
        </div>
      </div>
      <nav className="flex-1 p-2.5 space-y-0.5 overflow-y-auto">
        {nav.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            onClick={closeMobile}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive ? "bg-sky-50 text-sky-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`
            }
          >
            <Icon className="w-4 h-4" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-200">
        <div className="text-xs text-slate-500 mb-1">Signed in as</div>
        <div className="font-medium text-sm text-slate-900 truncate" data-testid="user-fullname">{user?.full_name}</div>
        <div className="text-[11px] text-slate-500 uppercase tracking-wider">{user?.role?.replace("_", " ")}</div>
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="mt-3 w-full flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 rounded-md py-1.5 hover:bg-slate-50 transition-colors"
        >
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </>
  );

  const activeNav = nav.find((n) => location.pathname.startsWith(n.to));
  // Show FAB only on these pages on mobile
  const fabRoutes = ["/dashboard", "/leads", "/map"];
  const showFab = fabRoutes.some((r) => location.pathname.startsWith(r));

  return (
    <div className="min-h-screen bg-[var(--sohom-bg)]">
      <header className="md:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-3 py-2 flex items-center gap-3" data-testid="mobile-topbar">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 -ml-1 rounded-md hover:bg-slate-100 active:bg-slate-200"
          aria-label="Open menu"
          data-testid="mobile-menu-toggle"
        >
          <Menu className="w-5 h-5 text-slate-700" />
        </button>
        <img src={LOGO_URL} alt="Sohom" className="h-6 w-auto" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-slate-900 truncate" style={{fontFamily:"'Outfit', sans-serif"}}>
            {activeNav?.label || "Sohom Lead Ops"}
          </div>
        </div>
      </header>

      <aside className="hidden md:flex w-60 bg-white border-r border-slate-200 flex-col fixed h-screen z-30 top-0 left-0" data-testid="app-sidebar">
        {Sidebar}
      </aside>

      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-slate-900/40 z-[9990]"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-drawer-backdrop"
          />
          <aside className="md:hidden fixed top-0 left-0 h-screen w-72 max-w-[85vw] bg-white z-[9999] flex flex-col shadow-2xl animate-[slideIn_180ms_ease-out]" data-testid="mobile-drawer">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-slate-100 z-10"
              aria-label="Close menu"
              data-testid="mobile-drawer-close"
            >
              <X className="w-5 h-5 text-slate-700" />
            </button>
            {Sidebar}
          </aside>
        </>
      )}

      <main className="md:ml-60">
        <OfflineBanner />
        {children}
      </main>

      {/* Mobile floating action button — capture a lead from anywhere */}
      {showFab && (
        <button
          onClick={openQuickLead}
          data-testid="capture-lead-fab"
          className="md:hidden fixed bottom-20 right-5 z-[9000] w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white active:scale-95 transition-transform"
          style={{ background: "linear-gradient(135deg, #0284C7 0%, #00AEEF 100%)" }}
          aria-label="Capture lead"
        >
          <Plus className="w-6 h-6" strokeWidth={2.5} />
        </button>
      )}

      {showQuickLead && (
        <LeadFormDialog
          users={fabUsers}
          autoGeolocate
          onClose={() => setShowQuickLead(false)}
          onSaved={() => {
            setShowQuickLead(false);
            if (location.pathname.startsWith("/leads")) {
              window.location.reload();
            } else {
              navigate("/leads");
            }
          }}
        />
      )}
    </div>
  );
}
